import { UserProfile } from '@lynx/core/src/interfaces/User'
import { Action, Dispatch } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { AuthCookie } from '../../modules/AuthCookie'
import { ApplicationState } from '../'
import { addBusy, removeBusy } from '../busy'
import { wsDisconnect } from '../messaging'
import {
	ACCEPTED_TERMS,
	Login,
	LOGIN,
	LOGOUT,
	ProfileActionTypes,
	CHANGE_PROFILE_CONTEXT_MENU,
	SET_DISPLAYNAME,
	SET_EMAIL,
	SET_LANG,
	SET_USER_SESSION_END,
	SET_PROFILE_PICTURE,
	setProfilePictureAction,
	SET_IS_PRO,
	SET_USER_PERMISSIONS
} from './types'
import { clearDrive } from '../drive/actions'
import { Logger } from '../../modules'
import { APIRequest } from '../../api'
import { ProfileReturn } from '../../api/interfaces'
import { PermissionsType } from '@lynx/core/src/interfaces'
import { getUserIdFromToken } from '@lynx/core/src/utils'

export const logoutInternal = (): ProfileActionTypes => ({
		type: LOGOUT
	})

export const setEmailInternal = (email: string): ProfileActionTypes => ({
		type: SET_EMAIL,
		payload: email
	})

export const setUserSessionEnd = (payload: boolean): ProfileActionTypes => ({
		type: SET_USER_SESSION_END,
		payload
	})

export const acceptedTerms = (): ProfileActionTypes => ({
		type: ACCEPTED_TERMS
	})

const setDisplayNameInternal = (displayName: string): ProfileActionTypes => ({
		type: SET_DISPLAYNAME,
		payload: displayName
	})

export const setLang = (lang: string): ProfileActionTypes => ({
		type: SET_LANG,
		payload: lang
	})

export const setIsPro = (payload: { isPro: boolean }): ProfileActionTypes => ({
		type: SET_IS_PRO,
		payload
	})

export const setUserPermissions = (payload: { permissions: PermissionsType }): ProfileActionTypes => ({
		type: SET_USER_PERMISSIONS,
		payload
	})

const loginInternal = (userProfile: UserProfile): ProfileActionTypes => ({
		type: LOGIN,
		payload: userProfile
	})

export const setProfilePicture = (data: setProfilePictureAction['payload']): ProfileActionTypes => ({
		type: SET_PROFILE_PICTURE,
		payload: data
	})

export const clientLogout =
	(): ThunkAction<Promise<void>, ApplicationState, null, Action<string>> =>
	async (dispatch: Dispatch, getState: () => ApplicationState): Promise<void> => {
		const isBlockingAction = true
		try {
			dispatch(addBusy(isBlockingAction))
			const { config } = getState()

			AuthCookie.delete('lynxbridge', config.ENVIRONMENT)

			dispatch(clearDrive())
			dispatch(logoutInternal())
		} catch (err) {
			Logger.error(err)
		} finally {
			dispatch(removeBusy(isBlockingAction))
		}
	}

export const logout =
	(): ThunkAction<Promise<void>, ApplicationState, null, Action<string>> =>
	async (dispatch: Dispatch, getState: () => ApplicationState): Promise<void> => {
		clientLogout()(dispatch, getState, null)
		try {
			await APIRequest.User.logout()
			await APIRequest.setAuthorization('')
			await dispatch(wsDisconnect())
		} catch (err) {
			Logger.error(err)
		}
	}

export const logoutEverywhere = (): ThunkAction<Promise<void>, ApplicationState, null, Action<string>> => async (): Promise<void> => {
	try {
		await APIRequest.User.logoutEverywhere()
	} catch (err) {
		Logger.error(err)
	}
}

export const setEmail =
	(email: string): ThunkAction<Promise<ProfileReturn>, ApplicationState, null, Action<string>> =>
	async (): Promise<ProfileReturn> => {
		try {
			await APIRequest.User.changeEmail(email)
			return {
				success: true
			}
		} catch (err: any) {
			const errMessage = err.response?.data?.message || 'Something went wrong'
			return { success: false, message: errMessage }
		}
	}

export const setDisplayName =
	(displayName: string): ThunkAction<Promise<ProfileReturn>, ApplicationState, null, Action<string>> =>
	async (dispatch: Dispatch): Promise<ProfileReturn> => {
		const isBlockingAction = true

		dispatch(addBusy(isBlockingAction))

		try {
			const changed: ProfileReturn = await APIRequest.User.changeDisplayName(displayName)

			if (changed.success) {
				dispatch(setDisplayNameInternal(displayName))
			}

			dispatch(removeBusy(isBlockingAction))

			return changed
		} catch (err) {
			dispatch(removeBusy(isBlockingAction))
			return { success: false, message: 'Something went wrong' }
		}
	}

export const login =
	(login: Login | null = null): ThunkAction<Promise<string>, ApplicationState, null, Action<string>> =>
	async (dispatch: Dispatch, getState: () => ApplicationState): Promise<string> => {
		const isBlockingAction = true

		dispatch(addBusy(isBlockingAction))
		try {
			if (!login) throw new Error('Login data is required')

			const { username, password, code } = login
			await logout()(dispatch, getState, null)
			const loginProfile = await APIRequest.User.login(username, password, code)

			if (loginProfile) {
				const { access_token: accessToken, user } = loginProfile
				APIRequest.setAuthorization(accessToken)
				dispatch(loginInternal(user))
				dispatch(removeBusy(isBlockingAction))
				return accessToken
			}

			return ''
		} catch (e) {
			const err = e as any
			Logger.error(e)

			const isServerDown = err.code === 'ERR_NETWORK'
			if (!isServerDown) logout()(dispatch, getState, null)

			dispatch(removeBusy(isBlockingAction))
			if (err.response?.data['login-failed-message']) {
				return err.response.data['login-failed-message']
			}
			return ''
		}
	}

export const changeProfileContextMenu = (open: boolean): ProfileActionTypes => ({
		type: CHANGE_PROFILE_CONTEXT_MENU,
		payload: {
			open
		}
	})

const getDeprecatedCookieTokens = (envCookie: string): { refreshToken: string; accessToken: string; userId: string } | null => {
	// this can be deleted 45 days after the release
	const cookie = AuthCookie.get('lynxbridge', envCookie)
	if (!cookie) return null

	const tokens = atob(cookie).split(' ')

	if (tokens.length < 2) return null
	const [cookieRefreshToken, cookieAccessToken] = tokens
	const userId = getUserIdFromToken(cookieAccessToken) || ''
	return {
		refreshToken: cookieRefreshToken,
		accessToken: cookieAccessToken,
		userId
	}
}

export const loginCookie =
	(): ThunkAction<Promise<string | void>, ApplicationState, null, Action<string>> =>
	async (dispatch: Dispatch, getState: () => ApplicationState): Promise<string | void> => {
		const isBlockingAction = true
		dispatch(addBusy(isBlockingAction))
		const envName = getState().config.ENVIRONMENT

		try {
			const renewPayload = getDeprecatedCookieTokens(envName)
			const { accessToken } = await APIRequest.User.renew(renewPayload)

			if (!accessToken) {
				Logger.error('failed to renew user session')
				throw new Error('failed to renew user session')
			}

			return await loginWithTokens({ accessToken })(dispatch, getState, null)
		} catch (e) {
			logout()(dispatch, getState, null)
			return ''
		} finally {
			AuthCookie.delete('lynxbridge', envName)
			dispatch(removeBusy(isBlockingAction))
		}
	}

export const loginWithTokenId =
	(tokenId: string): ThunkAction<Promise<string | void>, ApplicationState, null, Action<string>> =>
	async (dispatch: Dispatch, getState: () => ApplicationState): Promise<string | void> => {
		const isBlockingAction = true
		dispatch(addBusy(isBlockingAction))
		const tokens = await APIRequest.User.getTokensFromTokenId(tokenId)
		const { accessToken } = tokens
		const token = await loginWithTokens({ accessToken })(dispatch, getState, null)
		return token
	}

export const loginWithTokens =
	({ accessToken }: { accessToken: string | null }): ThunkAction<Promise<string>, ApplicationState, null, Action<string>> =>
	async (dispatch: Dispatch, getState: () => ApplicationState): Promise<string> => {
		if (!accessToken) {
			Logger.error('access token was empty')
			return ''
		}
		APIRequest.setAuthorization(accessToken)
		const userProfile = await APIRequest.User.profile()
		if (userProfile) {
			dispatch(loginInternal(userProfile))
			return accessToken
		}

		return ''
	}
